// Considered: the (now retired) property picker — extended in place instead of forked. This shell
// IS the extraction: the search box, scope banner, grouped-or-flat list, empty state
// and cancel button that picker already had, generalised so a second
// domain (PerspectivePickerMolecule) can reuse the same chrome instead of copying it.
// Domain-specific bits (row markup, bucket grouping rules, type-accent colors) stay
// with each caller; this component owns layout, search, and empty-state only.
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import SearchBarAtom from '../../atoms/SearchBar/SearchBarAtom';
import './PickerShellMolecule.scss';

export interface PickerShellBucket<T> {
  /** Stable bucket identity — becomes the React key. */
  key: string;
  label: string;
  /** Rendered inline after the group label (e.g. an "OUT OF SCOPE" tag). */
  badge?: React.ReactNode;
  /** Parity instrumentation — `data-datum` for the group label span (kind `heading`). */
  labelDatum?: string;
  /**
   * One-line explanation under the group label — "Inherited by every sphere type",
   * "Defined on this sphere type". The wireframe shows it on every group; it tells the
   * author WHY a perspective exists, which is what predicts whether it will have data.
   */
  note?: string;
  items: T[];
}

export interface PickerShellMoleculeProps<T> {
  /** The full, unfiltered item list. */
  items: T[];
  /** Text matched against the search query (lower-cased internally). */
  getSearchText: (item: T) => string;
  getRowKey: (item: T) => string;
  /**
   * Renders one row's CONTENT. In listbox mode (`onPickItem` supplied) the shell wraps
   * whatever this returns in the `role="option"` element and owns activation, so this
   * must NOT return a `<button>`, an `<a>` or anything else interactive — an option may
   * not contain interactive descendants (WAI-ARIA 1.2 § listbox).
   *
   * `grouped` tells the caller whether the item is inside a bucket head — some callers
   * show a redundant subtitle only in flat (ungrouped) mode.
   */
  renderRow: (item: T, ctx: { grouped: boolean; query: string }) => React.ReactNode;
  /**
   * Picks one item. Supplying it puts the shell in its canonical **combobox + listbox**
   * shape: the search field becomes the combobox that keeps focus, the list becomes the
   * listbox, each row is wrapped in a `role="option"`, and the shell owns click and
   * Enter activation plus the `aria-activedescendant` walk.
   *
   * Omitted, the shell keeps its legacy toolbar-of-row-buttons shape with roving DOM
   * focus — the only correct pattern for a caller whose rows carry a SECOND interactive
   * control (a nested link, a remove button), because such a row cannot be an option.
   */
  onPickItem?: (item: T) => void;
  /** Listbox mode — `aria-selected` for each option. Defaults to `false`. */
  isItemSelected?: (item: T) => boolean;
  /**
   * Listbox mode — an option that is listed but not choosable. It renders with
   * `aria-disabled`, ignores clicks, and is skipped by the active-index walk. The row
   * itself must still say WHY it cannot be picked (ux-craft I2).
   */
  isItemDisabled?: (item: T) => boolean;
  /**
   * Buckets the already-filtered item list. Return `null`/`undefined` to render a
   * flat list instead — e.g. when nothing in the filtered set carries a group.
   */
  computeBuckets?: (filtered: T[], ctx: { query: string }) => PickerShellBucket<T>[] | null | undefined;
  /**
   * Renders a bucket's heading. A caller whose contract owns the heading markup
   * (the sphere-type composer's attach dialog stamps `data-datum` on each family
   * head) supplies its own; everyone else gets the label + badge + note default.
   */
  renderBucketHead?: (bucket: PickerShellBucket<T>) => React.ReactNode;
  onCancel?: () => void;
  /**
   * The list's own accessible name — the listbox's, or the legacy toolbar's. Defaults to
   * `scopeLabel`. A `hideSearch` listbox is a tab stop in its own right, so it must be
   * named even when the caller states its scope elsewhere and passes no `scopeLabel`.
   */
  listLabel?: string;
  /**
   * The banner shown while the query is empty ("12 aspects in your registry").
   * Omit it when the caller states the scope itself — the attach dialog's count
   * line is a contract datum in its own container, so a second banner would say
   * the same thing twice.
   */
  scopeLabel?: string;
  /**
   * Seeds the search box when the shell owns its query. A story declaring a searched
   * reading must BE that reading when the capture is taken, not become it a keystroke later.
   */
  initialQuery?: string;
  loading?: boolean;
  loadingMessage?: React.ReactNode;
  /**
   * Renders a custom loading placeholder (e.g. skeleton rows) in place of the list.
   * Falls back to `loadingMessage` as plain text when omitted.
   */
  renderLoading?: () => React.ReactNode;
  /** Non-null renders this message instead of the loading/no-matches state. */
  errorMessage?: React.ReactNode | null;
  /** Bold lead line above `errorBody`/`errorMessage` when the failure needs a heading. */
  errorTitle?: string;
  /** Overrides `errorMessage` as the error body when both are present. */
  errorBody?: string;
  /** Retries the failed fetch. Rendered as an affordance under the error text when `retryLabel` is also set. */
  onRetry?: () => void;
  retryLabel?: string;
  /** Shown when nothing matches. `null` renders no empty state — the caller states it elsewhere. */
  noMatchesMessage: React.ReactNode;
  /**
   * Hides this shell's own search box, for a caller that already renders the
   * search input its contract places elsewhere and passes the filtered `items`.
   * The shell then contributes grouping, list layout and the empty state only.
   */
  hideSearch?: boolean;
  searchPlaceholder?: string;
  cancelLabel?: string;
  /** Rendered after the list, before the cancel affordance — e.g. a "search more" action. */
  listFooter?: React.ReactNode;
  /** Overrides the built-in scope banner content; gating (`alwaysShowScope` / hidden while searching) still applies. */
  scopeBanner?: React.ReactNode;
  /** Keeps the scope banner visible while the search query is non-empty. Defaults to hiding it. */
  alwaysShowScope?: boolean;
  /** Parity instrumentation — `data-datum` for the error callout's outer wrapper. */
  errorDatum?: string;
  /** Controlled search query. Omit to let the shell own its own query state. */
  query?: string;
  /**
   * Whether the shell narrows `items` by the query itself. Defaults to `true`. Pass `false`
   * when `items` is already the server's answer to `query` (with `onQueryChange` sending the
   * term to the search) — re-filtering by `getSearchText` would hide matches the server found
   * on text the row does not show.
   */
  filterItems?: boolean;
  onQueryChange?: (query: string) => void;
  /** Datum id for the search field's leading icon — set by callers whose prototype decomposition names it. */
  searchIconDatum?: string;
  /** Datum id for the search field's input element — set by callers whose prototype decomposition names it. */
  searchInputDatum?: string;
  /**
   * Rendered as the search field's trailing adornment — the prototype's `<kbd>Esc</kbd>`
   * hint (`advanced-filters-v2.jsx:543`) that tells the author the popover dismisses on
   * Escape. Omitted by default; a caller passes it only when `onCancel` makes the hint
   * true. Escape itself is wired below regardless, so the hint is never decorative.
   */
  escHint?: React.ReactNode;
  /**
   * Column-width reading: the shell's own chrome — scope banner, list padding and
   * height, group heads, footer affordances — steps down one spacing/type notch so the
   * list reads right at the ~420px a column popover or an inline drill-in actually has
   * (`advanced-filters-v2.jsx` `AFAspectCatalog` inline). Purely the shell's own
   * elements: the `SearchBarAtom` keeps its own scale, because an atom looks the same
   * wherever it is mounted (design-system.md Rule 10). Defaults to `false` — today's
   * full-size reading, unchanged.
   */
  dense?: boolean;
  testIdPrefix?: string;
  className?: string;
  /**
   * Parity instrumentation for the shell's own `role="option"` wrapper — listbox mode wraps
   * `renderRow`'s content in that element and owns its activation (see `renderRow`'s own doc),
   * so it is the actual actionable element a contract's `button`/`choice-card`/… affordance
   * datum names; the row content stays inert. Set `getOptionDatumId` + `getOptionKind`
   * together, and `getOptionContainer` when the contract nests the option under `each(...)`.
   */
  getOptionDatumId?: (item: T) => string | undefined;
  getOptionKind?: (item: T) => string | undefined;
  getOptionContainer?: (item: T) => string | undefined;
}

/**
 * Everything a browser will focus, so the legacy shape's roving focus is not limited to one
 * element type. A row whose only control is a checkbox (`ArchetypeMultiPickerMolecule`) or whose
 * root is itself the control (`role="button" tabIndex={0}`, `AspectAttachDialogOrganism`) used to
 * be unreachable by ↑/↓ because the walk queried `button` alone.
 */
const FOCUSABLE_SELECTOR = [
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * ONE focus stop per row, in rendered order — ↑/↓ move "between rows", never between the several
 * controls inside one row (a checkbox and its Primary radio are one row, not two stops). A row is
 * the element the caller's `renderRow` returned: in the legacy shape those are the children of the
 * list, or of a bucket's group when the list is grouped.
 */
function rowFocusStops(list: HTMLElement): HTMLElement[] {
  const stops: HTMLElement[] = [];
  const seenRows = new Set<Element>();
  list.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR).forEach((element) => {
    if (element.getAttribute('aria-disabled') === 'true') return;
    let row: HTMLElement = element;
    while (row.parentElement && row.parentElement !== list && !row.parentElement.classList.contains('picker-shell-molecule__group')) {
      row = row.parentElement;
    }
    if (seenRows.has(row)) return;
    seenRows.add(row);
    stops.push(element);
  });
  return stops;
}

const PickerShellMolecule = <T,>({
  items,
  getSearchText,
  getRowKey,
  renderRow,
  onPickItem,
  isItemSelected,
  isItemDisabled,
  computeBuckets,
  renderBucketHead,
  onCancel,
  listLabel,
  scopeLabel,
  initialQuery = '',
  loading = false,
  loadingMessage,
  renderLoading,
  errorMessage = null,
  errorTitle,
  errorBody,
  onRetry,
  retryLabel,
  noMatchesMessage,
  hideSearch = false,
  searchPlaceholder,
  cancelLabel,
  listFooter,
  scopeBanner,
  alwaysShowScope = false,
  errorDatum,
  query: controlledQuery,
  filterItems = true,
  onQueryChange,
  searchIconDatum,
  searchInputDatum,
  escHint,
  dense = false,
  testIdPrefix = 'picker-shell-option',
  className = '',
  getOptionDatumId,
  getOptionKind,
  getOptionContainer,
}: PickerShellMoleculeProps<T>) => {
  const [uncontrolledQuery, setUncontrolledQuery] = useState(initialQuery);
  const query = controlledQuery ?? uncontrolledQuery;
  const setQuery = onQueryChange ?? setUncontrolledQuery;
  const q = query.trim().toLowerCase();
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(
    () => (q && filterItems ? items.filter((item) => getSearchText(item).toLowerCase().includes(q)) : items),
    [items, q, getSearchText, filterItems]
  );

  const buckets = useMemo(() => computeBuckets?.(filtered, { query: q }) ?? null, [computeBuckets, filtered, q]);

  // A pending fetch or a failed one replaces the list outright — a stale row set
  // rendered underneath a spinner/error reads as a bug, not a state (EAINT-908).
  const showError = !loading && Boolean(errorMessage || errorTitle || errorBody);
  const showEmpty = !loading && !showError && filtered.length === 0;

  // Combobox + listbox is the canonical shape for "search a list, pick one value"
  // (WAI-ARIA 1.2 § combobox with list autocomplete). Focus never leaves the search
  // field; the active option is named by `aria-activedescendant` instead. A caller that
  // cannot be a listbox — rows carrying a second interactive control — omits
  // `onPickItem` and keeps the legacy toolbar-of-buttons roving-focus shape.
  const listboxMode = Boolean(onPickItem);
  const reactId = useId();
  const listId = `picker-shell-list-${reactId}`;
  const optionId = useCallback((index: number) => `picker-shell-option-${reactId}-${index}`, [reactId]);
  const bucketHeadId = (bucketKey: string) => `picker-shell-group-${reactId}-${bucketKey}`;
  const listAriaLabel = listLabel ?? scopeLabel;

  // The options in rendered order — buckets flattened, so one index addresses the same
  // option whether the list is grouped or flat.
  const visible = useMemo(
    () => (buckets ? buckets.flatMap((bucket) => bucket.items) : filtered),
    [buckets, filtered]
  );
  const isDisabledAt = useCallback(
    (index: number) => {
      const item = visible[index];
      return item === undefined || (isItemDisabled?.(item) ?? false);
    },
    [visible, isItemDisabled]
  );
  const firstEnabled = useMemo(() => {
    for (let i = 0; i < visible.length; i += 1) if (!isDisabledAt(i)) return i;
    return -1;
  }, [visible, isDisabledAt]);

  const [activeIndex, setActiveIndex] = useState(firstEnabled);
  // A new result set means a new active option: leaving the index where it was would
  // point `aria-activedescendant` at a row the search has just removed.
  useEffect(() => setActiveIndex(firstEnabled), [firstEnabled, visible]);

  const hasOptions = listboxMode && !loading && !showError && visible.length > 0;
  let listRole: 'listbox' | 'toolbar' | undefined;
  if (!listboxMode) listRole = 'toolbar';
  else if (hasOptions) listRole = 'listbox';
  else listRole = undefined;
  const activeOptionId = hasOptions && activeIndex >= 0 ? optionId(activeIndex) : undefined;

  // Keeps the active option in view. `aria-activedescendant` moves no DOM focus, so the
  // browser will not scroll for us; `block: 'nearest'` never re-centres a visible row.
  useEffect(() => {
    if (!activeOptionId) return;
    const element = typeof document !== 'undefined' ? document.getElementById(activeOptionId) : null;
    if (element && typeof element.scrollIntoView === 'function') element.scrollIntoView({ block: 'nearest' });
  }, [activeOptionId]);

  const pickAt = useCallback(
    (index: number) => {
      const item = visible[index];
      if (item === undefined || isDisabledAt(index)) return;
      onPickItem?.(item);
    },
    [visible, isDisabledAt, onPickItem]
  );

  // ArrowDown/ArrowUp/Home/End move the ACTIVE INDEX, never DOM focus, skipping
  // `aria-disabled` options.
  const moveActive = useCallback(
    (key: string) => {
      if (visible.length === 0) return;
      const forward = key !== 'ArrowUp' && key !== 'End';
      const direction = forward ? 1 : -1;
      let from: number;
      if (key === 'Home') from = -1;
      else if (key === 'End') from = visible.length;
      else if (activeIndex >= 0) from = activeIndex;
      else from = forward ? -1 : visible.length;
      for (let i = from + direction; i >= 0 && i < visible.length; i += direction) {
        if (!isDisabledAt(i)) {
          setActiveIndex(i);
          return;
        }
      }
    },
    [visible, activeIndex, isDisabledAt]
  );

  const handleNavKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Mirrors the prototype's own onKey (`advanced-filters-v2.jsx:524`) — Escape
      // dismisses the popover; the `escHint` kbd is only ever shown alongside this.
      if (event.key === 'Escape') {
        if (onCancel) {
          event.preventDefault();
          onCancel();
        }
        return;
      }
      if (!hasOptions) return;
      if (event.key === 'Enter') {
        if (activeIndex < 0) return;
        event.preventDefault();
        pickAt(activeIndex);
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      moveActive(event.key);
    },
    [onCancel, hasOptions, activeIndex, pickAt, moveActive]
  );

  // Legacy (non-listbox) shape: roving DOM focus across whatever focusable elements
  // renderRow produced, since the shell does not own that caller's row markup.
  const handleToolbarKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      if (onCancel) {
        event.preventDefault();
        onCancel();
      }
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const list = listRef.current;
    if (!list) return;
    const focusables = rowFocusStops(list);
    if (focusables.length === 0) return;
    event.preventDefault();
    const focusedIndex = focusables.indexOf(document.activeElement as HTMLElement);
    let nextIndex: number;
    if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = focusables.length - 1;
    else if (event.key === 'ArrowDown') nextIndex = focusedIndex < 0 ? 0 : Math.min(focusedIndex + 1, focusables.length - 1);
    else nextIndex = focusedIndex < 0 ? focusables.length - 1 : Math.max(focusedIndex - 1, 0);
    focusables[nextIndex]?.focus();
  }, [onCancel]);

  // The search input's own keys. In listbox mode it IS the combobox and owns the whole
  // walk; in the legacy shape ArrowDown hands DOM focus to the first row.
  const handleSearchKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (listboxMode) {
      handleNavKeyDown(event);
      return;
    }
    if (event.key === 'Escape' && onCancel) {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key === 'ArrowDown') {
      const list = listRef.current;
      const first = list ? rowFocusStops(list)[0] : undefined;
      if (first) {
        event.preventDefault();
        first.focus();
      }
    }
  }, [listboxMode, handleNavKeyDown, onCancel]);

  // Wraps one row in its option. The shell owns the option element, its identity, its
  // selected/disabled state and its activation; the caller owns only what is inside.
  let optionCursor = -1;
  const renderOption = (item: T, grouped: boolean) => {
    optionCursor += 1;
    const index = optionCursor;
    const disabled = isItemDisabled?.(item) ?? false;
    const optionDatumId = getOptionDatumId?.(item);
    return (
      <div
        key={getRowKey(item)}
        role="option"
        id={optionId(index)}
        aria-selected={isItemSelected?.(item) ?? false}
        aria-disabled={disabled || undefined}
        className={`picker-shell-molecule__option${index === activeIndex ? ' picker-shell-molecule__option--active' : ''}`}
        data-datum={optionDatumId}
        data-kind={optionDatumId ? getOptionKind?.(item) : undefined}
        data-container={optionDatumId ? getOptionContainer?.(item) : undefined}
        onClick={() => {
          if (disabled) return;
          setActiveIndex(index);
          onPickItem?.(item);
        }}
      >
        {renderRow(item, { grouped, query: q })}
      </div>
    );
  };

  const renderItem = (item: T, grouped: boolean) =>
    listboxMode ? (
      renderOption(item, grouped)
    ) : (
      <React.Fragment key={getRowKey(item)}>{renderRow(item, { grouped, query: q })}</React.Fragment>
    );

  return (
    <div className={`picker-shell-molecule${dense ? ' picker-shell-molecule--dense' : ''} ${className}`.trim()}>
      {!hideSearch && (
        <SearchBarAtom
          value={query}
          onChange={setQuery}
          placeholder={searchPlaceholder ?? ''}
          autoFocus
          className="picker-shell-molecule__search"
          iconDatum={searchIconDatum}
          inputDatum={searchInputDatum}
          rightAdornment={escHint}
          onKeyDown={handleSearchKeyDown}
          // The search box carries no visible label — the one case `aria-label` is the
          // right answer (ux-craft F1). The scope banner is the fallback name for a
          // caller that supplies no placeholder.
          ariaLabel={searchPlaceholder || scopeLabel}
          role={listboxMode ? 'combobox' : undefined}
          ariaAutocomplete={listboxMode ? 'list' : undefined}
          ariaControls={listboxMode ? listId : undefined}
          ariaExpanded={listboxMode ? hasOptions : undefined}
          ariaActivedescendant={activeOptionId}
        />
      )}
      {(alwaysShowScope || !q) && (scopeBanner ?? (scopeLabel && (
        <div className="picker-shell-molecule__scope-banner">
          <i className="pi pi-filter" aria-hidden="true" />
          <span className="picker-shell-molecule__scope-label">{scopeLabel}</span>
          <span className="picker-shell-molecule__scope-count">{items.length}</span>
        </div>
      )))}
      {/* The listbox the combobox controls. It takes the listbox role only while it
          actually holds options: a loading, failed or empty list owns a status message,
          not options, and `role="listbox"` over that is both a lie and an
          `aria-required-children` violation. `hideSearch` callers have no combobox, so
          the listbox itself is the tab stop and owns `aria-activedescendant`.
          Without `onPickItem` the shell keeps its legacy vertical-toolbar shape. */}
      <div
        className="picker-shell-molecule__list"
        // The list is a collection (grouped or flat) and each group is a collection of
        // rows — the prototype's `each(shown) > each(g.items)` / `each(typeIds) > each(…)`,
        // which PAR-5 requires the render to declare.
        data-container="list > each(entries)"
        ref={listRef}
        id={listboxMode ? listId : undefined}
        role={listRole}
        aria-orientation={listboxMode ? undefined : 'vertical'}
        // `aria-label` is prohibited on a role-less element — while this list is holding a
        // status message rather than options it is exactly that, so the name goes too.
        aria-label={listRole ? listAriaLabel : undefined}
        tabIndex={listboxMode && hideSearch && hasOptions ? 0 : undefined}
        aria-activedescendant={listboxMode && hideSearch ? activeOptionId : undefined}
        onKeyDown={listboxMode ? (hideSearch ? handleNavKeyDown : undefined) : handleToolbarKeyDown}
      >
        {loading && (
          renderLoading ? renderLoading() : (
            <div className="picker-shell-molecule__loading" data-testid={`${testIdPrefix}-loading-message`}>
              {loadingMessage}
            </div>
          )
        )}
        {showError && (
          <div
            className="picker-shell-molecule__error"
            data-testid={`${testIdPrefix}-error`}
            data-datum={errorDatum}
            data-kind={errorDatum ? 'callout' : undefined}
          >
            {errorTitle && <div className="picker-shell-molecule__error-title">{errorTitle}</div>}
            <div className="picker-shell-molecule__error-body">{errorBody ?? errorMessage}</div>
            {onRetry && retryLabel && (
              <button
                type="button"
                className="picker-shell-molecule__retry"
                onClick={onRetry}
                data-testid={`${testIdPrefix}-retry`}
              >
                {retryLabel}
              </button>
            )}
          </div>
        )}
        {!loading && !showError && (
          <>
            {buckets
              ? buckets.map((bucket) => (
                  // One bucket is one item of the list: its heading and the rows under it travel
                  // together, and the rows are the bucket's own collection.
                  <div
                    key={bucket.key}
                    className="picker-shell-molecule__group"
                    data-container="each(buckets) > list-item > each(items)"
                    // A listbox may own nothing but options and groups — a heading or a
                    // note sitting among them is an `aria-required-children` failure. So
                    // the head stays exactly where it is visually and leaves the
                    // accessibility tree (`aria-hidden`), and the group takes its full
                    // text — label, badge and note — as its own name via
                    // `aria-labelledby`, which reads a hidden element on purpose. Nothing
                    // a screen reader had is lost; it arrives as the group's name instead.
                    role={hasOptions ? 'group' : undefined}
                    aria-labelledby={hasOptions ? bucketHeadId(bucket.key) : undefined}
                  >
                    <div
                      id={hasOptions ? bucketHeadId(bucket.key) : undefined}
                      aria-hidden={hasOptions || undefined}
                      className="picker-shell-molecule__group-head-slot"
                    >
                      {renderBucketHead ? (
                        renderBucketHead(bucket)
                      ) : (
                        <>
                          {(bucket.label || bucket.badge) && (
                            <div className="picker-shell-molecule__group-head">
                              <span
                                className="picker-shell-molecule__group-label"
                                data-datum={bucket.labelDatum}
                                data-kind={bucket.labelDatum ? 'heading' : undefined}
                              >
                                {bucket.label}
                              </span>
                              {bucket.badge}
                            </div>
                          )}
                          {bucket.note && (
                            <div className="picker-shell-molecule__group-note">{bucket.note}</div>
                          )}
                        </>
                      )}
                    </div>
                    {bucket.items.map((item) => renderItem(item, true))}
                  </div>
                ))
              : filtered.map((item) => renderItem(item, false))}
            {showEmpty && noMatchesMessage != null && (
              <div className="picker-shell-molecule__empty" data-testid={`${testIdPrefix}-empty`}>
                {noMatchesMessage}
              </div>
            )}
          </>
        )}
      </div>
      {listFooter}
      {onCancel && (
        <button
          type="button"
          className="picker-shell-molecule__cancel"
          onClick={onCancel}
          data-testid={`${testIdPrefix}-cancel-pick`}
        >
          {cancelLabel}
        </button>
      )}
    </div>
  );
};

PickerShellMolecule.displayName = 'PickerShellMolecule';

export default PickerShellMolecule;
